/**
 * What the owner overlay, the pinned widget windows and Rust agree on for
 * frames pinned to the desktop (plan 12 §4). Rects are logical points in
 * global desktop coordinates, the space the display work areas use.
 */
import { invoke } from "@tauri-apps/api/core";
import {
  listen,
  type EventCallback,
  type UnlistenFn,
} from "@tauri-apps/api/event";
import { getCurrentWindow } from "@tauri-apps/api/window";
import type { PinnedWidgetSize } from "@/components/shapes/shared/widget-sizing";
import type { FrameData } from "@/lib/frame-data";

export interface PinRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Something already on the desktop that a widget must not cover: another
 *  app's widget (its visible card), a desktop icon or another shown pin. */
export interface OccupiedRect extends PinRect {
  kind: "widget" | "icon" | "pin";
}

/**
 * The native material Rust put behind the transparent pin webview (plan 12
 * §8): Liquid Glass (macOS), Acrylic (Windows 11), or none, so the widget
 * paints its own solid card.
 */
export type PinMaterial = "glass" | "acrylic" | "solid";

const PIN_MATERIALS: readonly PinMaterial[] = ["glass", "acrylic", "solid"];

export function isPinMaterial(value: unknown): value is PinMaterial {
  return PIN_MATERIALS.includes(value as PinMaterial);
}

/** The native window of a frame pinned to the desktop. */
export function pinWindowLabel(frameId: string): string {
  return `pin-${frameId}`;
}

/**
 * Hears `event` only when it is sent to this pin window (`emitTo` its
 * label): a listener for any target would hear every pin's events.
 */
export function listenInPinWindow<T>(
  event: string,
  handler: EventCallback<T>,
): Promise<UnlistenFn> {
  return listen<T>(event, handler, {
    target: { kind: "WebviewWindow", label: getCurrentWindow().label },
  });
}

/** The init-script stamp a pin window carries (`desktop_pin_window.rs`). */
export interface PinWindowStamp {
  frameId: string;
  projectId: string;
  material: PinMaterial;
}

export const DESKTOP_PIN_EVENTS = {
  /** widget → owner: send me my frame. */
  ready: "denker://desktop-pin-ready",
  /** widget → owner: a move or resize settled (already snapped to an allowed size). */
  rect: "denker://desktop-pin-rect",
  /** widget → owner: Unpin from desktop. */
  unpin: "denker://desktop-pin-unpin",
  /** widget → owner: Open (the frame in Focus). */
  open: "denker://desktop-pin-open",
  /** owner → widget: the frame's full detail, on ready and on every update. */
  frame: "denker://desktop-pin-frame",
  /** Rust → widget: the material differs from the stamp's (dressing failed). */
  material: "denker://desktop-pin-material",
} as const;

export interface DesktopPinIdPayload {
  frameId: string;
}

export interface DesktopPinRectPayload {
  frameId: string;
  rect: PinRect;
  size: PinnedWidgetSize;
}

export interface DesktopPinFramePayload {
  frameId: string;
  /**
   * The pin's project now: the owner retargets the pin when its frame moves
   * (`retargetDesktopPinRecord`), so the widget follows. Never read from
   * `frame.spaceId`, which some paths fill with the canvas space.
   */
  projectId: string;
  frame: FrameData;
}

export interface DesktopPinMaterialPayload {
  frameId: string;
  material: PinMaterial;
}

export async function openDesktopPin(
  frameId: string,
  projectId: string,
  rect: PinRect,
): Promise<void> {
  await invoke("open_desktop_pin", { frameId, projectId, rect });
}

export async function closeDesktopPin(frameId: string): Promise<void> {
  await invoke("close_desktop_pin", { frameId });
}

/**
 * Moves and sizes the pin window at once (snap settle, menu grow/shrink).
 * `plate`, in window-local logical points, is where the widget sits inside
 * a window grown around its menu: the native material covers only that;
 * without it the material fills the window. Answers the material behind
 * the widget from then on (Windows paints solid while the window is grown).
 */
export async function setDesktopPinRect(
  frameId: string,
  rect: PinRect,
  plate?: PinRect,
): Promise<PinMaterial> {
  return invoke<PinMaterial>("set_desktop_pin_rect", { frameId, rect, plate });
}

/** The theme the native material is drawn in (macOS rebuilds its glass). */
export async function setDesktopPinAppearance(
  frameId: string,
  dark: boolean,
): Promise<void> {
  await invoke("set_desktop_pin_appearance", { frameId, dark });
}

export async function desktopOccupiedRects(): Promise<OccupiedRect[]> {
  return invoke<OccupiedRect[]>("desktop_occupied_rects");
}

/**
 * Whether the primary mouse button is down now: a native move or resize is
 * still under way (Windows; always false on macOS).
 */
export async function desktopPinPrimaryButtonDown(): Promise<boolean> {
  return invoke<boolean>("desktop_pin_primary_button_down");
}
